import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, BRAND, formatApiError } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";

export function MagicLinkSetup() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Invalid link."); return; }
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/magic-link", { token, new_password: pw });
      toast.success("Password set. Welcome in.");
      await refresh?.();
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Setup failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell>
      <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">Welcome to Dr Thrive 365</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-3">Set your password.</h1>
      <p className="text-white/55 mt-4 max-w-md">
        Create a password so you can sign in whenever you want. Your session with Dr. Slava is already confirmed.
      </p>
      <form onSubmit={submit} className="glass rounded-2xl p-6 mt-8 border border-white/10 space-y-4" data-testid="magic-setup-form">
        <div>
          <label className="text-[11px] tracking-[0.2em] uppercase text-white/50 block mb-2">New password</label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="At least 8 characters" data-testid="setup-pw"
            className="bg-black/40 border-white/10 focus:border-[#FF8A00] h-11" />
        </div>
        <div>
          <label className="text-[11px] tracking-[0.2em] uppercase text-white/50 block mb-2">Confirm password</label>
          <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
            data-testid="setup-pw2"
            className="bg-black/40 border-white/10 focus:border-[#FF8A00] h-11" />
        </div>
        <Button type="submit" disabled={saving}
          data-testid="setup-submit"
          className="w-full rounded-full h-12 bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-60">
          {saving ? <><Loader2 className="animate-spin mr-2" size={14}/> Signing you in…</> : "Set password & open dashboard"}
        </Button>
        <div className="flex items-center justify-center gap-2 text-[11px] text-white/40">
          <ShieldCheck size={12} /> Encrypted at rest — you can change it anytime.
        </div>
      </form>
    </Shell>
  );
}

export function BookingSuccessPublic() {
  const [params] = useSearchParams();
  const intakeId = params.get("intake_id");
  const payPalToken = params.get("token"); // PayPal order ID
  const [capturing, setCapturing] = useState(!!payPalToken);
  const [captureError, setCaptureError] = useState("");

  useEffect(() => {
    if (!payPalToken) return;
    (async () => {
      try {
        await api.post("/payments/paypal/capture", { order_id: payPalToken });
      } catch (e) {
        setCaptureError(formatApiError(e?.response?.data?.detail) || "We couldn't confirm your payment yet. Please contact support if you were charged.");
      } finally {
        setCapturing(false);
      }
    })();
  }, [payPalToken]);

  return (
    <Shell>
      <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center">
        {capturing ? <Loader2 size={22} className="animate-spin" /> : <CheckCircle2 size={22} />}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl mt-6 text-center">
        {capturing ? "Confirming your payment…" : captureError ? "Hmm — one sec." : "Your session is booked."}
      </h1>
      {captureError ? (
        <p className="text-white/60 mt-4 text-center max-w-md mx-auto" data-testid="paypal-capture-error">{captureError}</p>
      ) : (
        <p className="text-white/60 mt-4 text-center max-w-md mx-auto">
          Thanks — we&apos;ve emailed you a confirmation and a magic link to set your password
          and access the platform. Your calendar invite from Dr. Slava will follow within 24 hours.
        </p>
      )}
      {intakeId && (
        <p className="text-[11px] text-white/30 text-center mt-3 font-mono">Ref: {intakeId.slice(0, 8)}</p>
      )}
      <div className="flex justify-center mt-8">
        <Link to="/">
          <Button variant="outline" className="rounded-full bg-transparent border-white/15 hover:bg-white/5">
            Back to home
          </Button>
        </Link>
      </div>
    </Shell>
  );
}

export function BookingCancelPublic() {
  return (
    <Shell>
      <h1 className="font-display text-4xl mt-6 text-center">Payment cancelled.</h1>
      <p className="text-white/60 mt-4 text-center max-w-md mx-auto">
        No charge was made. Your intake was saved — you can come back and finish anytime.
      </p>
      <div className="flex justify-center gap-3 mt-8">
        <Link to="/book-session"><Button className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">Try again</Button></Link>
        <Link to="/"><Button variant="outline" className="rounded-full bg-transparent border-white/15">Home</Button></Link>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white relative">
      <nav className="fixed top-0 inset-x-0 z-40 glass">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Dr Thrive 365" className="h-9 w-9 rounded-md object-cover" />
            <span className="font-display text-xl">Dr Thrive 365</span>
          </Link>
        </div>
      </nav>
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/3 w-[500px] h-[500px] rounded-full bg-[#FF8A00]/10 blur-[160px]" />
        <div className="absolute bottom-0 -right-40 w-[420px] h-[420px] rounded-full bg-[#E52E71]/10 blur-[140px]" />
      </div>
      <section className="relative pt-32 pb-24 px-4 sm:px-8">
        <div className="max-w-2xl mx-auto">{children}</div>
      </section>
    </div>
  );
}
